/**
 * Locale- and timezone-aware date formatting utilities.
 *
 * TIMEZONE MODEL (EQUIPR Core):
 * - Operational instants (e.g. stop `arrived_at`/`completed_at`) are stored in
 *   the backend as UTC ("...Z" / "...+00:00"). They must ALWAYS be displayed in
 *   the tenant's operating timezone so everyone — regardless of the viewer's
 *   browser location — sees the same wall-clock time as the PDF.
 * - Wall-clock values stored WITHOUT a timezone (e.g. depot depart/arrival
 *   "2026-09-01T06:00:00") are already tenant-local and are shown verbatim.
 * - Date-only strings ("2026-04-03") are treated as local calendar dates.
 *
 * Set the tenant timezone once per session with `setDisplayTimeZone(tz)` (done
 * from the tenant-settings / auth bootstrap). When unset, formatting falls back
 * to the browser's local timezone (legacy behaviour).
 */

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Session-wide display timezone (IANA name, e.g. "Australia/Adelaide").
let _displayTimeZone = null;

/** Set the timezone all UTC instants are rendered in (tenant's operating tz). */
export function setDisplayTimeZone(tz) {
  _displayTimeZone = tz || null;
}

/** Current display timezone, or null when falling back to browser-local. */
export function getDisplayTimeZone() {
  return _displayTimeZone;
}

// A string carries an explicit timezone if it ends in Z or ±HH:MM / ±HHMM.
function _hasTimeZone(value) {
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
}

// Re-position a real instant so its LOCAL getters return the wall-clock in the
// display timezone. Falls back to the raw instant (browser-local) when unset.
function _shiftInstantToDisplay(date) {
  if (!_displayTimeZone) return date;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: _displayTimeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(date);
    const p = {};
    for (const { type, value } of parts) p[type] = value;
    let hour = parseInt(p.hour, 10);
    if (hour === 24) hour = 0; // some engines emit '24' for midnight
    return new Date(+p.year, +p.month - 1, +p.day, hour, +p.minute, +p.second || 0);
  } catch {
    return date;
  }
}

/**
 * Convert any accepted value into a Date whose LOCAL components equal the
 * intended DISPLAY wall-clock:
 *  - Date object / tz-aware string (UTC) -> shifted into the display timezone
 *  - naive datetime string -> shown as written (already tenant-local)
 *  - date-only string -> local calendar date
 */
export function toDisplayDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : _shiftInstantToDisplay(value);
  if (typeof value !== 'string') return null;

  if (!value.includes('T')) {
    const d = new Date(value + 'T00:00:00'); // date-only -> local midnight
    return isNaN(d.getTime()) ? null : d;
  }
  if (!_hasTimeZone(value)) {
    const d = new Date(value); // naive -> parsed local, components == written wall-clock
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value); // tz-aware instant
  return isNaN(d.getTime()) ? null : _shiftInstantToDisplay(d);
}

/**
 * Parse a date string into a local Date object (date-only safe).
 * Retained for backwards compatibility; prefer `toDisplayDate`.
 */
export function toLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return null;
  return value.includes('T') ? new Date(value) : new Date(value + 'T00:00:00');
}

// --- internal component formatters (read a display-positioned Date's LOCAL parts) ---

function _fmtDate(d, dateFormat = 'DD/MM/YYYY') {
  if (!d || isNaN(d.getTime())) return '-';
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const monthShort = MONTH_SHORT[d.getMonth()];
  switch (dateFormat) {
    case 'MM/DD/YYYY':  return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':  return `${year}-${month}-${day}`;
    case 'DD-MMM-YYYY': return `${day}-${monthShort}-${year}`;
    case 'DD/MM/YYYY':
    default:            return `${day}/${month}/${year}`;
  }
}

function _fmtTime(d, timeFormat = '24h') {
  if (!d || isNaN(d.getTime())) return '';
  const hours = d.getHours();
  const minutes = d.getMinutes();
  if (timeFormat === '12h') {
    const period = hours >= 12 ? 'PM' : 'AM';
    const h12 = hours % 12 || 12;
    return `${h12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Format a date according to the tenant's date_format.
 * "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD" | "DD-MMM-YYYY"
 */
export function formatDate(value, dateFormat = 'DD/MM/YYYY') {
  if (!value) return '-';
  return _fmtDate(toDisplayDate(value), dateFormat);
}

/** Long date for headers. AU: "Thursday, 2 April 2026"  US: "Thursday, April 2, 2026" */
export function formatDateLong(value, dateFormat = 'DD/MM/YYYY') {
  const d = toDisplayDate(value);
  if (!d || isNaN(d.getTime())) return '';
  const dayName = DAY_LONG[d.getDay()];
  const day = d.getDate();
  const monthName = MONTH_LONG[d.getMonth()];
  const year = d.getFullYear();
  if (dateFormat === 'MM/DD/YYYY') return `${dayName}, ${monthName} ${day}, ${year}`;
  return `${dayName}, ${day} ${monthName} ${year}`;
}

/** Short date (no year). AU: "2 Apr"  US: "Apr 2" */
export function formatDateShort(value, dateFormat = 'DD/MM/YYYY') {
  const d = toDisplayDate(value);
  if (!d || isNaN(d.getTime())) return '';
  const day = d.getDate();
  const month = MONTH_SHORT[d.getMonth()];
  if (dateFormat === 'MM/DD/YYYY') return `${month} ${day}`;
  return `${day} ${month}`;
}

/** Short date WITH year. AU: "2 Apr 2026"  US: "Apr 2, 2026" */
export function formatDateShortWithYear(value, dateFormat = 'DD/MM/YYYY') {
  const d = toDisplayDate(value);
  if (!d || isNaN(d.getTime())) return '';
  const day = d.getDate();
  const month = MONTH_SHORT[d.getMonth()];
  const year = d.getFullYear();
  if (dateFormat === 'MM/DD/YYYY') return `${month} ${day}, ${year}`;
  return `${day} ${month} ${year}`;
}

/** Weekday + short date, e.g. "Sat, 2 Apr" (AU) or "Sat, Apr 2" (US). */
export function formatWeekdayShort(value, dateFormat = 'DD/MM/YYYY') {
  const d = toDisplayDate(value);
  if (!d || isNaN(d.getTime())) return '';
  const wd = DAY_SHORT[d.getDay()];
  const mo = MONTH_SHORT[d.getMonth()];
  const day = d.getDate();
  if (dateFormat === 'MM/DD/YYYY') return `${wd}, ${mo} ${day}`;
  return `${wd}, ${day} ${mo}`;
}

/** Combined date + time in the tenant timezone. e.g. "03/04/2026 14:30". */
export function formatDateTime(value, dateFormat = 'DD/MM/YYYY', timeFormat = '24h') {
  if (!value) return '-';
  const d = toDisplayDate(value);
  if (!d || isNaN(d.getTime())) return '-';
  return `${_fmtDate(d, dateFormat)} ${_fmtTime(d, timeFormat)}`;
}

/**
 * Format a time string ("HH:mm") or ISO datetime per the tenant's time_format.
 * Plain "HH:mm" values are shown verbatim; ISO/tz-aware values render in the
 * tenant timezone.
 */
export function formatTime(timeStr, timeFormat = '24h') {
  if (!timeStr) return '';

  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (timeFormat === '12h') {
      const period = hours >= 12 ? 'PM' : 'AM';
      const h12 = hours % 12 || 12;
      return `${h12}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  if (timeStr.includes('T') || timeStr.includes('-')) {
    const d = toDisplayDate(timeStr);
    if (!d || isNaN(d.getTime())) return timeStr;
    return _fmtTime(d, timeFormat);
  }

  return timeStr;
}

/** Format a Date/ISO value's time portion in the tenant timezone. */
export function formatTimeFromDate(dateValue, timeFormat = '24h') {
  if (!dateValue) return '';
  const d = toDisplayDate(dateValue);
  if (!d || isNaN(d.getTime())) return '';
  return _fmtTime(d, timeFormat);
}
